import { getRegionFromPathname, getWebsiteFromRegion } from './website'

const DEFAULT_POSTHOG_HOST = 'https://eu.i.posthog.com'
const READY_EVENT_NAME = 'zeti:posthog-ready'

let clientPromise = null

const isBrowser = () => typeof window !== 'undefined'

const getCurrentPostHogContext = () => {
  const siteRegion = getRegionFromPathname(window.location.pathname)
  return {
    siteRegion,
    website: getWebsiteFromRegion(siteRegion),
  }
}

const applySiteProperties = (client) => {
  const { siteRegion, website } = getCurrentPostHogContext()
  const properties = {
    site_region: siteRegion,
    website,
  }

  client.register(properties)

  if (typeof client.setPersonPropertiesForFlags === 'function') {
    client.setPersonPropertiesForFlags(properties, true)
  }

  return properties
}

export async function initPostHog() {
  if (!isBrowser()) return null
  if (window.__zetiPostHogClient) return window.__zetiPostHogClient
  if (clientPromise) return clientPromise

  const apiKey = process.env.GATSBY_POSTHOG_KEY
  if (!apiKey) return null

  clientPromise = import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(apiKey, {
        api_host: process.env.GATSBY_POSTHOG_HOST || DEFAULT_POSTHOG_HOST,
        persistence: 'localStorage+cookie',
        loaded: (client) => {
          applySiteProperties(client)
          window.__zetiPostHogClient = client
          window.dispatchEvent(new CustomEvent(READY_EVENT_NAME))
        },
      })

      window.__zetiPostHogClient = posthog
      applySiteProperties(posthog)
      return posthog
    })
    .catch((error) => {
      console.error('Failed to initialize PostHog', error)
      return null
    })

  return clientPromise
}

export function getPostHogClient() {
  if (!isBrowser()) return null
  return window.__zetiPostHogClient || window.posthog || null
}

export function getPostHogFeatureFlag(key) {
  const client = getPostHogClient()
  if (!client || typeof client.getFeatureFlag !== 'function') return undefined
  return client.getFeatureFlag(key)
}

export function onPostHogReady(callback) {
  if (!isBrowser()) return () => {}

  const readyHandler = () => {
    const client = getPostHogClient()
    if (client) callback(client)
  }

  if (getPostHogClient()) {
    readyHandler()
    return () => {}
  }

  window.addEventListener(READY_EVENT_NAME, readyHandler)
  return () => window.removeEventListener(READY_EVENT_NAME, readyHandler)
}

export function onPostHogFeatureFlags(callback) {
  let cleanupFlags = () => {}
  let hasRegistered = false

  const registerListener = (client) => {
    if (!client || typeof client.onFeatureFlags !== 'function' || hasRegistered) return
    hasRegistered = true
    cleanupFlags = client.onFeatureFlags(() => callback(client))
    callback(client)
  }

  const cleanupReady = onPostHogReady(registerListener)
  const existingClient = getPostHogClient()

  if (existingClient) {
    registerListener(existingClient)
  }

  return () => {
    cleanupFlags()
    cleanupReady()
  }
}

export function capturePostHogEvent(event, properties) {
  const client = getPostHogClient()
  if (!client || typeof client.capture !== 'function') return false

  client.capture(event, properties)
  return true
}
